import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Col, Container, Row, Spinner } from "react-bootstrap"
import CustomCard from "../../components/customCard"
import { ContainerSignUpAdmin, ContainerSignUpButtons } from "../../components/renderField/styles"
import { StyledBackButtonAdmin, StyledConfirmationCodeText, StyledContainerButtons, StyledContainerParagraph, StyledParagraphConfirmationCode, StyledLoginButton, StyledSwitchButton } from "../signup/styles"
import VerificationInput from "react-verification-input"
import './styles.css'
import { useToast } from "../../context/Toast"
import { useAuth } from "../../context/AuthContext"
import { confirmCellphoneAuth, startCellphoneAuth } from "../../services/user/user"
import { getCompanyId } from "../../services/company/company"
import { UserRole, UserStatus } from "../../enums/user"
import { clearResendRequest, getResendCooldown, saveResendRequest } from "./resendCooldown"

import { useTranslation } from 'react-i18next';
const ConfirmationCodeAuthAdmin: React.FC = () => {
    const { t } = useTranslation('auth');
    const navigate = useNavigate()
    const location = useLocation()
    const { showToast } = useToast()
    const { saveDataVerifyOnStorage, saveDataSignUpOnStorage } = useAuth()
    const [loading, setLoading] = useState(false)
    const [resendLoading, setResendLoading] = useState(false)
    const [savedValue, setSavedValue] = useState('')
    const cellphone = location.state?.cellphone
    const [session, setSession] = useState(location.state?.session)
    const [resendCooldown, setResendCooldown] = useState(() => cellphone ? getResendCooldown(cellphone) : 0)

    useEffect(() => {
        if (resendCooldown <= 0) return

        const timer = window.setInterval(() => {
            setResendCooldown((currentCooldown) => Math.max(currentCooldown - 1, 0))
        }, 1000)

        return () => window.clearInterval(timer)
    }, [resendCooldown])

    useEffect(() => {
        if (!cellphone || typeof session !== 'string' || !session) {
            navigate('/admin/cellphone-auth', { replace: true })
        }
    }, [cellphone, navigate, session])

    const login = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        if (loading || resendLoading || savedValue.length !== 8 || !cellphone || !session) {
            showToast(t('verification.codeComplete'), 'error', 5000)
            return
        }
        setLoading(true)
        try {
            const loginData = await confirmCellphoneAuth(cellphone, savedValue, session)
            const allowedRole = [UserRole.sAdmin, UserRole.admin, UserRole.group_responsible].includes(Number(loginData.role))
            if (loginData.status !== UserStatus.active || !allowedRole) {
                showToast(t('errors.managementPanelPermission'), 'error', 8000)
                return
            }

            await saveDataVerifyOnStorage({ verifyToken: loginData.verifyToken })
            const company = (await getCompanyId(loginData.company_id))?.Item
            const dataToSave: any = {
                id: loginData.id,
                status: loginData.status,
                first_name: loginData.first_name,
                last_name: loginData.last_name,
                role: loginData.role,
                cellphone,
                company_visited: company,
                user_company: company,
            }
            if (loginData.email) dataToSave.email = loginData.email

            await saveDataSignUpOnStorage(dataToSave)
            clearResendRequest(cellphone)
            showToast(t('verification.authenticated'), 'success', 6000)
            navigate('/admin')
        } catch (error: any) {
            if (error?.response?.status === 401) {
                showToast(t('verification.invalidCode'), 'error', 7000)
            } else {
                showToast(t('errors.generic'), 'error', 30000)
            }
        } finally {
            setLoading(false)
        }
    }

    const resend = async () => {
        if (resendLoading || loading || resendCooldown > 0 || !cellphone) return
        setResendLoading(true)
        try {
            const authData = await startCellphoneAuth(cellphone)
            if (authData?.challengeName !== 'SMS_OTP' || typeof authData?.session !== 'string' || !authData.session) {
                showToast(t('errors.smsResend'), 'error', 7000)
                return
            }
            setSession(authData.session)
            setSavedValue('')
            setResendCooldown(saveResendRequest(cellphone))
            showToast(t('verification.resent'), 'success', 6000)
        } catch (error: any) {
            showToast(t('errors.smsResend'), 'error', 8000)
        } finally {
            setResendLoading(false)
        }
    }

    const formattedCooldown = `${Math.floor(resendCooldown / 60).toString().padStart(2, '0')}:${(resendCooldown % 60).toString().padStart(2, '0')}`;
    return <Container fluid className="d-flex h-100 mt-5 mb-2"><Row className="justify-content-center align-self-center w-100"><Col md={9} lg={7}><CustomCard>
        {loading ? <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '300px' }}><Spinner style={{ color: '#c9c9c9' }} /></div> : <ContainerSignUpAdmin>
            <StyledConfirmationCodeText style={{ color: '#3C4043' }}>{t('verification.smsSent')}</StyledConfirmationCodeText><StyledParagraphConfirmationCode style={{ color: '#3C4043' }}>{t('verification.codePrompt')}</StyledParagraphConfirmationCode>
            <div className="d-flex justify-content-center" style={{ width: '100%', flexDirection: 'column' }}><VerificationInput length={8} validChars="0-9" value={savedValue} onChange={setSavedValue} inputProps={{ inputMode: "numeric" }} placeholder="" classNames={{ container: "container-admin", character: "character-admin" }} /></div>
            <StyledContainerParagraph />
            <StyledSwitchButton style={{ color: '#3C4043', textDecoration: resendCooldown > 0 ? 'none' : 'underline', opacity: resendCooldown > 0 ? 0.5 : 1 }} disabled={resendLoading || loading || resendCooldown > 0} onClick={resend}>{resendLoading ? t('verification.resendLoading') : resendCooldown > 0 ? t('verification.resendCooldown', { time: formattedCooldown }) : t('verification.resend')}</StyledSwitchButton>
            <ContainerSignUpButtons><StyledContainerButtons><StyledBackButtonAdmin onClick={() => navigate('/admin/cellphone-auth')}>{t('signIn.back')}</StyledBackButtonAdmin><StyledLoginButton disabled={loading || resendLoading} onClick={login}>{t('signIn.submit')}</StyledLoginButton></StyledContainerButtons></ContainerSignUpButtons>
        </ContainerSignUpAdmin>}
    </CustomCard></Col></Row></Container>
}

export default ConfirmationCodeAuthAdmin;
